/**
 * THE PRIVACY POLICY, WRITTEN ONCE (PV5a) — prest0.ai/privacy-policy, in today's design (src/pages/Privacy.tsx) and in
 * the new design's reading page (src/design/pavilion/pages/privacy-policy/). See ./legalDocument.ts for how a document is
 * shared.
 *
 * Moved here from Privacy.tsx as written, part names aside; the page's rendered HTML was compared byte for byte before
 * and after the move. The date it took effect is src/config/legalVersions.ts's (EffectiveDate), and a no-JavaScript
 * copy is published as public/privacy-policy.html (kept by hand; scripts/check-legal-effective-date.mjs explains why).
 *
 * /signup links to /privacy-policy#sms: the SMS section keeps its own id for the contents and carries a second address,
 * `sms`, drawn by the `Anchor` part (see the PRE-72 note in that section).
 */
import React from 'react';
import type { LegalDocument, LegalParts } from './legalDocument';

/** What the page says above the document, and what its contents are called. */
export const PRIVACY_POLICY_HEAD = {
  heading: 'privacy policy',
  contentsTitle: 'on this page',
  contentsName: 'privacy policy table of contents',
} as const;

/** The key takeaways the page shows before the document. */
export const PRIVACY_POLICY_SUMMARY = {
  badge: 'key takeaways',
  heading: 'we protect your data so you can build ai agents with confidence',
  items: [
    'no selling or renting of personal information—ever.',
    'tls 1.3, aes-256, oauth 2.0, and gcp soc 2 type ii controls secure every workspace.',
    'prompts, transcripts, and attachments never train shared models.',
    'you can download a copy of your data, and ask us to delete it, from your settings page. a deletion request tells you what will go and what we have to keep. a deletion request can be cancelled for 30 days; we carry it out after that window closes, and in any case within 45 days of the request, except where a legal hold or legal, tax or regulatory duties require us to keep records.',
  ],
} as const;

export type PrivacyPolicyParts = Pick<
  LegalParts,
  'Text' | 'List' | 'ListItem' | 'SubTitle' | 'PageLink' | 'OutLink' | 'Facts' | 'Fact' | 'FactLabel' | 'FactValue' | 'FactNote' | 'Note' | 'NoteText' | 'Anchor'
>;

/** The privacy policy, in the parts the caller draws it with. */
export function privacyPolicyDocument({
  Text, List, ListItem, SubTitle, PageLink, OutLink, Facts, Fact, FactLabel, FactValue, FactNote, Note, NoteText, Anchor,
}: PrivacyPolicyParts): LegalDocument {
  return {
    sections: [
      {
        id: 'overview',
        heading: 'overview',
        body: (
          <>
            <Text>
              prest0 ("prest0", "we", "our", or "us") builds ai agent workflows that help teams ship automations without code. This privacy policy explains how we collect, use, store, and share personal information for visitors, builders, investors, and partners who interact with the live experience at prest0.ai and any related services that link to this notice (collectively, the "services").
            </Text>
            <Text>
              By using the services, you agree to the practices described here as well as in our <PageLink to="/terms-of-service">terms of service</PageLink> and <PageLink to="/cookie-policy">cookie policy</PageLink>. If you disagree with this policy, please discontinue use of the services.
            </Text>
            <Text>
              The privacy commitments below match what we publish on the security page: TLS 1.3 in transit, AES-256 encryption at rest on google cloud, sign-in through Google or an email and password with permissions resolved per person, documented retention schedules with deletion on request, and a contractual no-training guarantee for every prompt handled by enterprise AI endpoints.
            </Text>
            <Facts>
              <Fact>
                <FactLabel>data controller</FactLabel>
                <FactValue>prest0</FactValue>
                <FactNote>applies to every workspace</FactNote>
              </Fact>
              <Fact>
                <FactLabel>security baseline</FactLabel>
                <FactValue>tls 1.3 + aes-256</FactValue>
                <FactNote>matches the controls in our security page</FactNote>
              </Fact>
              <Fact>
                <FactLabel>retention controls</FactLabel>
                <FactValue>deletion on request</FactValue>
                <FactNote>cancellable for 30 days, carried out within 45</FactNote>
              </Fact>
            </Facts>
            <Text>
              This policy applies whether you access prest0 directly, participate in private beta programs, engage with us through live demonstrations, or communicate with our team via chat, email, or social channels.
            </Text>
          </>
        ),
      },
      {
        id: 'information-we-collect',
        heading: 'information we collect',
        body: (
          <>
            <SubTitle>information you provide to us</SubTitle>
            <List>
              <ListItem>account details such as name, email address, company, and authentication credentials when you create or manage a prest0 account.</ListItem>
              <ListItem>profile preferences, notification settings, and any configuration data you save inside your workspace.</ListItem>
              <ListItem>support requests, survey responses, chat conversations, and other communications with our team.</ListItem>
              <ListItem>billing contact data and limited payment-related metadata when you engage in paid features (payment details are stored securely by our payment processor).</ListItem>
            </List>
            <SubTitle>information collected automatically</SubTitle>
            <List>
              <ListItem>log data such as browser type, device information, operating system, and ip address used to access the services.</ListItem>
              <ListItem>server logs from the requests your browser makes — IP address, browser metadata and timestamps. We run no analytics product, so we do not build a record of pages you viewed, features you clicked, how long you stayed, or where you came from.</ListItem>
              <ListItem>when you are signed in, the chat reports its own health to our servers so we can find and fix problems you run into: counts and timings such as how long a thread took to load, how often the page froze, for how long, which part of our own code or of the browser's own storage was busy and how large the page was, and whether a message failed to start, tagged with your account and workspace. it never includes what you write, file names, or the pages you visit, and it is kept only in our server logs. if you are not signed in, none of it leaves your browser.</ListItem>
              <ListItem>if you chat with Prest0n before creating an account — on this site or on a widget embedded elsewhere — we keep that conversation for 30 days after your last message — the clock restarts each time you come back, so it is still there when you return and can be carried into your account if you sign up. stop using it and it is deleted automatically; you can also ask us to remove it straight away from our trust page, without an account. It is keyed to an irreversible hash of your IP address; we do not store the address itself. Nothing about it is shared with an advertiser, because we have none.</ListItem>
              <ListItem>cookie identifiers and similar technologies used to remember preferences, maintain session security, and (with consent) measure engagement.</ListItem>
              <ListItem>diagnostic data, crash reports, and trace logs generated automatically by our infrastructure providers.</ListItem>
            </List>
            <SubTitle>information from third parties</SubTitle>
            <List>
              <ListItem>authentication providers (such as google) that share tokens or profile basics when you opt in to single sign-on.</ListItem>
              <ListItem>payment processors (stripe) that confirm billing status, card brand, and the last four digits of payment methods.</ListItem>
              <ListItem>service partners that help deliver functionality (for example, cloud hosting or ai model providers) where permitted by their policies and yours.</ListItem>
            </List>
            <SubTitle>information generated by ai agents</SubTitle>
            <List>
              <ListItem>prompt content and agent outputs that you explicitly save to your workspace.</ListItem>
              <ListItem>system telemetry that helps us monitor performance, latency, and error recovery for automated workflows.</ListItem>
              <ListItem>anonymized or aggregated insights derived from agent runs that cannot be traced back to an individual user.</ListItem>
            </List>
            <SubTitle>information collected via sms/text messaging</SubTitle>
            <List>
              <ListItem>phone number you provide when opting in to receive text messages from prest0.</ListItem>
              <ListItem>message content, delivery status, and opt-in/opt-out preferences associated with your mobile number.</ListItem>
              <ListItem>carrier information used for message routing and delivery confirmation.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'how-we-use-information',
        heading: 'how we use information',
        body: (
          <>
            <Text>
              We process personal information so we can operate a secure, reliable, and continuously improving ai agent experience. Specifically, we use data to:
            </Text>
            <List>
              <ListItem>deliver core product functionality, including authentication, workspace management, collaboration, and agent execution.</ListItem>
              <ListItem>support you with onboarding, documentation, success guidance, and customer care.</ListItem>
              <ListItem>maintain platform health, detect anomalies, prevent fraud, and troubleshoot issues.</ListItem>
              <ListItem>research and build new capabilities, including personalization and automation improvements.</ListItem>
              <ListItem>communicate service updates, security alerts, or administrative announcements.</ListItem>
              <ListItem>send transactional and informational text messages to your mobile phone when you opt in, such as case updates, appointment reminders, and account notifications.</ListItem>
              <ListItem>comply with legal obligations, enforce agreements, and protect the rights of prest0 and our users.</ListItem>
            </List>
            <Note>
              <NoteText>
                We do not sell personal information. We only use data to support the services you request, and we honor your consent choices for analytics and marketing.
              </NoteText>
            </Note>
          </>
        ),
      },
      {
        id: 'legal-bases',
        heading: 'legal bases for processing',
        body: (
          <>
            <Text>
              prest0 serves customers in the United States today. Where a data protection law such as the gdpr or a comparable regulation applies to you, we rely on these legal bases:
            </Text>
            <List>
              <ListItem>performance of a contract when supplying requested services, maintaining your account, or providing support.</ListItem>
              <ListItem>legitimate interests to secure our infrastructure, improve features, and understand product adoption in a privacy-aware manner.</ListItem>
              <ListItem>compliance with legal obligations, such as tax, accounting, or regulatory requirements.</ListItem>
              <ListItem>consent for marketing communications, beta programs, or processing that is not strictly necessary. We run no optional analytics, so none is collected on this basis.</ListItem>
              <ListItem>protection of vital interests when responding to urgent safety matters or preventing harmful activity.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'information-sharing',
        heading: 'how we share information',
        body: (
          <>
            <Text>
              We share personal information only with trusted parties that help us operate or when you direct us to do so:
            </Text>
            <List>
              <ListItem>infrastructure and product vendors (google cloud / firebase, auth providers, customer support tooling) that follow strict data protection terms.</ListItem>
              <ListItem>payment partners (stripe) that securely manage billing and financial transactions.</ListItem>
              <ListItem>enterprise AI execution partners. These vendors must honor contractual no-training and no-retention guarantees before any data is routed their way.</ListItem>
              <ListItem>legal authorities when required by law, court order, or to defend our rights.</ListItem>
              <ListItem>future corporate transactions, such as a merger or acquisition, where your information remains protected by this policy or a successor policy with equal safeguards.</ListItem>
            </List>
            <Note>
              <NoteText>
                We do not permit partners to use your personal information for their own independent marketing. Every vendor agreement includes confidentiality and security commitments.
              </NoteText>
            </Note>
          </>
        ),
      },
      {
        id: 'data-retention',
        heading: 'data retention',
        body: (
          <>
            <Text>
              We retain personal information only for as long as it is needed to deliver the services, meet legal requirements, or resolve disputes. Retention follows our documented operational schedule, and we delete your data on request; per-workspace retention settings are on the roadmap, not shipped. When data is no longer required, we delete or anonymize it in line with documented schedules.
            </Text>
            <List>
              <ListItem>account data persists while your workspace remains active and for a short grace period if you deactivate the account.</ListItem>
              <ListItem>agent prompts, transcripts and attachments are kept under our documented retention schedule and deleted on request. per-workspace retention timers are on the roadmap, not shipped.</ListItem>
              <ListItem>support tickets and chat transcripts are retained for quality assurance and legal compliance, typically no longer than 24 months.</ListItem>
              <ListItem>operational metrics that cannot be tied to an individual — error rates, latency, model usage totals — may be kept to inform long-term product strategy.</ListItem>
            </List>
            <Text>
              You may request deletion of your personal data at any time. A deletion request can be cancelled for 30 days; we carry it out after that window closes, and in any case within 45 days of the request, except where a legal hold or legal, tax or regulatory duties require us to keep records.
            </Text>
          </>
        ),
      },
      {
        id: 'sms-text-messaging',
        heading: 'sms & text messaging',
        body: (
          <>
            {/* PRE-72: /signup links to /privacy-policy#sms. We keep the existing
                section id ('sms-text-messaging') so the in-page TOC and scroll-spy
                keep working, and expose an additional 'sms' anchor inside the
                section body so the shorter URL still scrolls here. */}
            <Anchor id="sms" />
            <Text>
              prest0 uses Twilio to send SMS text messages from (628) 444-4018. By providing your phone number and opting in, you consent to receive text messages from prest0 at the number provided. The following disclosures apply to all SMS communications:
            </Text>
            <List>
              <ListItem>message frequency varies based on your account activity and notification preferences.</ListItem>
              <ListItem>message and data rates may apply. Check with your mobile carrier for applicable rates.</ListItem>
              <ListItem>you can opt out at any time by replying STOP to any message. You will receive one final confirmation message and then no further texts.</ListItem>
              <ListItem>for help, reply HELP to any message or contact us at <OutLink href="mailto:support@prest0.ai">support@prest0.ai</OutLink>.</ListItem>
              <ListItem>we do not share your phone number with third parties for their own marketing purposes.</ListItem>
              <ListItem>consent to receive text messages is not a condition of purchase or use of any prest0 service.</ListItem>
              <ListItem>supported carriers include major US carriers. Carrier message and data rates may apply. Carriers are not liable for delayed or undelivered messages.</ListItem>
            </List>
            <Note>
              <NoteText>
                Text messages from prest0 are transactional and informational in nature—case updates, appointment reminders, and account notifications. We do not send unsolicited marketing texts. Your opt-in consent can be withdrawn at any time by replying STOP.
              </NoteText>
            </Note>

            {/* PRE-72: carrier-compliance-grade SMS disclosures, additive to the
                existing copy above. Twilio A2P reviewers specifically asked us
                to spell out (i) what SMS-specific data we collect, (ii) why,
                (iii) who we share with, (iv) opt-out paths, and (v) retention. */}
            <SubTitle>what sms data we collect</SubTitle>
            <List>
              <ListItem>phone number (normalized to E.164) you provide at opt-in.</ListItem>
              <ListItem>consent timestamp recorded by our servers (UTC).</ListItem>
              <ListItem>IP address captured from your network request at the time of opt-in.</ListItem>
              <ListItem>user-agent string from the browser or device used to opt in.</ListItem>
              <ListItem>double-opt-in confirmation status (pending / confirmed / opted-out / expired).</ListItem>
            </List>
            <SubTitle>why we collect it</SubTitle>
            <List>
              <ListItem>carrier-compliance audit trails for A2P 10DLC, TCPA, and similar regulations.</ListItem>
              <ListItem>routing and delivery of account notifications you explicitly requested.</ListItem>
              <ListItem>fraud prevention and abuse detection (e.g. catching bulk opt-in abuse).</ListItem>
            </List>
            <SubTitle>who we share your phone number with</SubTitle>
            <List>
              <ListItem>Twilio (SMS delivery processor under a signed DPA) and mobile network operators in the delivery path.</ListItem>
              <ListItem>nobody else. We do not share or sell your phone number to third-party marketers, data brokers, or advertisers.</ListItem>
            </List>
            <SubTitle>how to opt out</SubTitle>
            <List>
              <ListItem>text STOP (or STOPALL / UNSUBSCRIBE / CANCEL / END / QUIT) to any Prest0n message.</ListItem>
              <ListItem>toggle SMS off in your account settings at any time.</ListItem>
              <ListItem>email <OutLink href="mailto:support@prest0.ai">support@prest0.ai</OutLink> to request manual removal.</ListItem>
            </List>
            <SubTitle>retention of sms data</SubTitle>
            <List>
              <ListItem>your phone number is removed from active send lists within 24 hours of opt-out.</ListItem>
              <ListItem>the consent audit record (timestamp, IP, UA, opt-in/opt-out events) is retained for as long as carrier-compliance regulations require it. See the dedicated <PageLink to="/sms-terms">SMS Terms</PageLink> page for the full program description.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'data-security',
        heading: 'data security',
        body: (
          <>
            <Text>
              Keeping your data safe is fundamental to our platform design. We implement the same layered safeguards described on the security page, including:
            </Text>
            <List>
              <ListItem>TLS 1.3 locks down every session from browser to api, and we disable legacy protocols.</ListItem>
              <ListItem>data stored in google cloud firestore and storage is encrypted with aes-256 plus managed key rotation.</ListItem>
              <ListItem>sign-in through google or an email and password, role-based permissions resolved per person and per message, and scoped api keys enforce least-privilege access. two-step verification for every member is planned, not shipped.</ListItem>
              <ListItem>append-only audit logs and automated alerts provide oversight. we commit to notify affected customers within 48 hours of confirming a security incident; the notice goes to each workspace's owners by email and in the app, and a check runs every hour until every affected workspace has received it.</ListItem>
              <ListItem>enterprise ai endpoints run under contractual no-training guarantees so prompts never fine-tune shared models.</ListItem>
              <ListItem>we inherit soc 2 type ii and iso/iec 27001 controls from google cloud. prest0 has not completed its own audit or penetration test yet; both are planned.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'ai-automation',
        heading: 'ai agents & automation',
        body: (
          <>
            <Text>
              prest0 offers ai-powered automation. We apply guardrails to ensure responsible use of data within these workflows:
            </Text>
            <List>
              <ListItem>agents operate inside isolated sandboxes with per-field data protections and debounced persistence to avoid overwriting active edits.</ListItem>
              <ListItem>we log the minimum metadata necessary to debug agent performance while respecting user privacy preferences.</ListItem>
              <ListItem>when you connect external data sources, we process the information strictly according to your documented instructions.</ListItem>
              <ListItem>automated decisions that may significantly affect users always include human review and override options.</ListItem>
              <ListItem>no-training guarantees mean prompts, transcripts, and attachments never fine-tune shared models; they remain inside governed enterprise endpoints.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'international-transfers',
        heading: 'international transfers',
        body: (
          <>
            <Text>
              Customer production data is stored on Google Cloud in the United States: our databases and application services run in Google Cloud's us-central1 region (Iowa), and some older Windows workstations still run in us-east4 (Virginia) while they are moved to us-central1. An AI model provider we use may process a request elsewhere; each one, and where it runs, is listed on our subprocessors page.
            </Text>
            <Text>
              Our processing is in the United States. Where a cross-border transfer is in scope for you, we rely on approved transfer mechanisms—such as standard contractual clauses and vendor-specific safeguards—and we monitor regulatory updates to remain compliant.
            </Text>
          </>
        ),
      },
      {
        id: 'privacy-choices',
        heading: 'your privacy choices',
        body: (
          <>
            <Text>
              You remain in control of your personal information. Depending on your jurisdiction, you may have the right to:
            </Text>
            <List>
              <ListItem>access, review, or export a copy of the data we maintain about you.</ListItem>
              <ListItem>update or correct inaccurate information via account settings or by contacting support.</ListItem>
              <ListItem>request deletion or anonymization of your personal data.</ListItem>
              <ListItem>opt out of non-essential communications and withdraw consent for analytics at any time.</ListItem>
              <ListItem>restrict or object to certain processing activities where legally permitted.</ListItem>
            </List>
            <Note>
              <NoteText>
                Submit privacy requests through the in-product workspace settings or live chat. We verify each request inside the app, then keep an auditable record so you can confirm completion.
              </NoteText>
            </Note>
          </>
        ),
      },
      {
        id: 'cookies',
        heading: 'cookies & tracking technologies',
        body: (
          <>
            <Text>
              We use cookies and local storage to keep you signed in and to remember how you have set things up. We set no advertising or analytics cookie and load no third-party tracker, so there is no consent banner here and nothing non-essential for you to decline. Your browser settings refuse or delete what we do set, and we honor Global Privacy Control.
            </Text>
            <Text>
              For a detailed breakdown of cookie categories, retention windows, and opt-out controls, visit our dedicated <PageLink to="/cookie-policy">cookie policy</PageLink>.
            </Text>
          </>
        ),
      },
      {
        id: 'third-parties',
        heading: 'third-party services & integrations',
        body: (
          <>
            <Text>
              To deliver the services, we partner with carefully vetted providers. Key partners include:
            </Text>
            <List>
              <ListItem>google cloud / firebase for secure hosting, authentication, firestore database, and storage.</ListItem>
              <ListItem>stripe for payment processing and financial reporting.</ListItem>
              <ListItem>enterprise AI endpoints that execute prompts with strict no-training guarantees and signed DPAs.</ListItem>
              <ListItem>operations tooling (support systems, logging, and analytics) that operate under strict confidentiality clauses.</ListItem>
            </List>
            <Text>
              Each provider is required to implement industry-standard security controls and to use your data only as instructed by prest0.
            </Text>
          </>
        ),
      },
      {
        id: 'policy-updates',
        heading: 'updates to this policy',
        body: (
          <>
            <Text>
              We review this privacy policy regularly to reflect new features, regulatory changes, or operational updates. When we make material changes, we will post a notice on the services, update the effective date above, and provide additional communication if required by law.
            </Text>
          </>
        ),
      },
    ],
  };
}
